// src/pages/Dashboard.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  ButtonBase,
  Box,
  useTheme,
  useMediaQuery
} from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import TuneIcon from "@mui/icons-material/Tune";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const userName = localStorage.getItem("userName") || "";
  const companyName = localStorage.getItem("companyName") || t('dashboard.title');

  const shortcuts = [
    { name: t('dashboard.purchases'), icon: <ShoppingCartIcon />, gradient: 'linear-gradient(135deg, #f44336, #e57373)', link: "/purchases-menu" },
    { name: t('dashboard.sales'), icon: <ShoppingBagIcon />, gradient: 'linear-gradient(135deg, #ff9800, #ffb74d)', link: "/sales-menu" },
    { name: t('dashboard.warehouses'), icon: <WarehouseIcon />, gradient: 'linear-gradient(135deg, #4caf50, #81c784)', link: "/warehouses-menu" },
    { name: t('dashboard.accounting'), icon: <AccountBalanceIcon />, gradient: 'linear-gradient(135deg, #2196f3, #64b5f6)', link: "/accounts-menu" },
    { name: t('dashboard.definitions'), icon: <CategoryIcon />, gradient: 'linear-gradient(135deg, #3f51b5, #7986cb)', link: "/company-definitions" },
    { name: t('dashboard.pos'), icon: <PointOfSaleIcon />, gradient: 'linear-gradient(135deg, #9c27b0, #ba68c8)', link: "/pos" },
    { name: t('dashboard.generalSettings'), icon: <TuneIcon />, gradient: 'linear-gradient(135deg, #ff4081, #f48fb1)', link: "/settings-menu" },
    //{ name: t('common.change_password'), icon: <LockResetIcon />, gradient: 'linear-gradient(135deg, #607d8b, #90a4ae)', link: "/change-password" },
    //{ name: t('common.change_username'), icon: <PersonIcon />, gradient: 'linear-gradient(135deg, #795548, #a1887f)', link: "/change-username" },
   // { name: t('common.change_mobile'), icon: <PhoneIcon />, gradient: 'linear-gradient(135deg, #009688, #4db6ac)', link: "/change-mobile" }
  ];

  return (
    <Container maxWidth={false} sx={{ mt: 4, px: 2 }}>
      {/* رسالة الترحيب */}
      {userName && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <span>أهلاً بك،</span>
            <span style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>{userName}</span>
            <span>👋</span>
          </Typography>
        </Box>
      )}

      {/* العنوان */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant={isMobile ? "h4" : "h2"}
          sx={{
            background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {companyName}
        </Typography>
      </Box>

      {/* شبكة الاختصارات */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(auto-fit, minmax(140px, 1fr))',
            md: 'repeat(auto-fit, minmax(160px, 1fr))',
            lg: 'repeat(auto-fit, minmax(180px, 1fr))'
          },
          justifyItems: 'center',
        }}
      >
        {shortcuts.map((item, i) => (
          <ButtonBase
            key={i}
            onClick={() => navigate(item.link)}
            sx={{
              width: '100%',
              aspectRatio: '1 / 1',
              borderRadius: 3,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: 2,
              transition: 'all 0.3s ease',
              background: item.gradient,
              color: 'white',
              "&:hover": { 
                boxShadow: 6, 
                transform: 'translateY(-3px)', 
                filter: 'brightness(1.1)'
              },
              "&:active": { transform: 'translateY(0px)', boxShadow: 2 }
            }}
          >
            {React.cloneElement(item.icon, { sx: { fontSize: 45, mb: 1 } })}
            <Typography
              variant="subtitle2"
              sx={{
                mt: 1,
                fontWeight: 'bold',
                textAlign: 'center',
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }
              }}
            >
              {item.name}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    </Container>
  );
}
